/**
 * Module 1: First-Time Onboarding Wizard
 * 
 * 9-step wizard with progress tracking, auto-save, and resume capability
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  CheckCircle2,
  ChevronRight,
  ChevronLeft,
  Sparkles
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STEPS = [
  { id: 1, title: 'Welcome', description: 'Get started with MedSpaBookings OS' },
  { id: 2, title: 'Business Profile', description: 'Tell us about your business' },
  { id: 3, title: 'Team Members', description: 'Invite your team' },
  { id: 4, title: 'Integrations', description: 'Connect your tools' },
  { id: 5, title: 'Lead Import', description: 'Import your leads' },
  { id: 6, title: 'First AI Audit', description: 'Generate your first audit' },
  { id: 7, title: 'First Proposal', description: 'Create your first proposal' },
  { id: 8, title: 'Launch Checklist', description: 'Final preparation' },
  { id: 9, title: 'Completion', description: 'You\'re all set!' }
];

const OnboardingWizard = ({ user, onNavigate }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [progress, setProgress] = useState(null);
  const [saving, setSaving] = useState(false);

  const authHeaders = useCallback(() => ({
    'Authorization': `Bearer ${user.token}`,
    'Content-Type': 'application/json'
  }), [user.token]);

  const loadProgress = useCallback(async () => {
    try {
      const response = await fetch(`${API}/onboarding/progress`, {
        headers: authHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setProgress(data);
        setCurrentStep(data.current_step || 1);
        setCompletedSteps(data.completed_steps || []);
      }
    } catch (error) {
      console.error('Error loading progress:', error);
    }
  }, [authHeaders]);

  useEffect(() => {
    loadProgress();
  }, [loadProgress]);

  // Takes the values to persist explicitly: setCurrentStep/setCompletedSteps
  // don't update this render's state, so reading them here saved the
  // previous step - "Finish" stored 8 of 9 steps and onboarding never
  // reached 100% (or its completed_at).
  const saveProgress = async (step, completed) => {
    setSaving(true);
    try {
      await fetch(`${API}/onboarding/progress`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          current_step: step,
          completed_steps: completed
        })
      });
    } catch (error) {
      console.error('Error saving progress:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleNext = async () => {
    const nextCompleted = completedSteps.includes(currentStep) ? completedSteps : [...completedSteps, currentStep];
    const nextStep = currentStep < STEPS.length ? currentStep + 1 : currentStep;
    setCompletedSteps(nextCompleted);
    setCurrentStep(nextStep);
    await saveProgress(nextStep, nextCompleted);
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = async () => {
    const nextStep = currentStep < STEPS.length ? currentStep + 1 : currentStep;
    setCurrentStep(nextStep);
    await saveProgress(nextStep, completedSteps);
  };

  const progressPercentage = (completedSteps.length / STEPS.length) * 100;

  return (
    <div className="space-y-6" data-testid="onboarding-wizard">
      {/* Progress Bar */}
      <Card className="glass-panel border-white/10">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-white/70">Onboarding Progress</span>
            <span className="text-sm font-semibold text-[var(--gold)]">{Math.round(progressPercentage)}%</span>
          </div>
          <Progress value={progressPercentage} className="h-2" />
          <div className="flex items-center justify-between mt-2">
            <span className="text-xs text-white/60">{completedSteps.length} of {STEPS.length} steps completed</span>
            {saving && <span className="text-xs text-white/60">Saving...</span>}
          </div>
        </CardContent>
      </Card>

      {/* Steps Navigation */}
      <div className="flex items-center gap-2 overflow-x-auto pb-2">
        {STEPS.map((step) => (
          <button
            key={step.id}
            onClick={() => setCurrentStep(step.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all whitespace-nowrap ${
              currentStep === step.id
                ? 'bg-[var(--gold)]/20 border-[var(--gold)] text-[var(--gold)]'
                : completedSteps.includes(step.id)
                ? 'bg-green-500/20 border-green-500/30 text-green-300'
                : 'bg-white/5 border-white/10 text-white/60'
            }`}
            data-testid={`step-button-${step.id}`}
          >
            {completedSteps.includes(step.id) && <CheckCircle2 size={16} />}
            <span className="text-sm">{step.id}. {step.title}</span>
          </button>
        ))}
      </div>

      {/* Current Step Content */}
      <Card className="glass-panel border-white/10">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <Badge className="mb-2">Step {currentStep} of {STEPS.length}</Badge>
              <CardTitle className="text-2xl text-white/90">{STEPS[currentStep - 1].title}</CardTitle>
              <p className="text-white/70 mt-1">{STEPS[currentStep - 1].description}</p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Step-specific content */}
          {currentStep === 1 && (
            <div className="space-y-4">
              <p className="text-white/80">Welcome to MedSpaBookings OS! This guided wizard will help you set up your account and get started with our platform.</p>
              <div className="p-4 bg-[var(--gold)]/10 border border-[var(--gold)]/30 rounded-lg">
                <h4 className="font-semibold text-[var(--gold)] mb-2">What you'll accomplish:</h4>
                <ul className="space-y-1 text-sm text-white/80">
                  <li>• Set up your business profile</li>
                  <li>• Invite team members</li>
                  <li>• Connect integrations</li>
                  <li>• Import your leads</li>
                  <li>• Generate your first AI audit</li>
                  <li>• Create your first proposal</li>
                </ul>
              </div>
            </div>
          )}

          {currentStep === 2 && (
            <div className="space-y-4">
              <Input placeholder="Business Name" className="bg-white/5 border-white/10" />
              <Input placeholder="Website" className="bg-white/5 border-white/10" />
              <Input placeholder="Industry" className="bg-white/5 border-white/10" />
              <Input placeholder="Location" className="bg-white/5 border-white/10" />
            </div>
          )}

          {currentStep === 3 && (
            <div className="space-y-4">
              <p className="text-white/80">Invite team members to collaborate on leads, audits, and proposals.</p>
              <Button className="ghost-btn" onClick={() => onNavigate?.('team')} data-testid="onboarding-goto-team-btn">
                Go to Team Management
              </Button>
            </div>
          )}

          {currentStep === 4 && (
            <div className="space-y-4">
              <p className="text-white/80">Connect your tools and services to streamline your workflow.</p>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 border border-white/10 rounded-lg">
                  <h4 className="font-semibold text-white/90 mb-1">Stripe</h4>
                  <p className="text-xs text-white/60">Payment processing</p>
                </div>
                <div className="p-4 border border-white/10 rounded-lg">
                  <h4 className="font-semibold text-white/90 mb-1">SendGrid</h4>
                  <p className="text-xs text-white/60">Email automation</p>
                </div>
              </div>
            </div>
          )}

          {currentStep === 5 && (
            <div className="space-y-4">
              <p className="text-white/80">Import your existing leads or start with a CSV file.</p>
              <Button className="ghost-btn" onClick={() => onNavigate?.('leads')} data-testid="onboarding-goto-leads-btn">
                Go to Lead Database
              </Button>
            </div>
          )}

          {currentStep === 6 && (
            <div className="space-y-4">
              <p className="text-white/80">Generate your first AI-powered audit to demonstrate value to prospects.</p>
              <Button className="primary-btn" onClick={() => onNavigate?.('audits')} data-testid="onboarding-goto-audits-btn">
                <Sparkles size={16} />Go to Audit Center
              </Button>
            </div>
          )}

          {currentStep === 7 && (
            <div className="space-y-4">
              <p className="text-white/80">Create your first professional proposal with AI assistance.</p>
              <Button className="primary-btn" onClick={() => onNavigate?.('proposal_generator')} data-testid="onboarding-goto-proposals-btn">
                <Sparkles size={16} />Go to Proposal Generator
              </Button>
            </div>
          )}

          {currentStep === 8 && (
            <div className="space-y-4">
              <p className="text-white/80">Review your setup and ensure everything is ready for launch.</p>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="text-green-400" size={16} />
                  <span className="text-sm text-white/80">Business profile complete</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="text-green-400" size={16} />
                  <span className="text-sm text-white/80">Team members invited</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="text-green-400" size={16} />
                  <span className="text-sm text-white/80">Integrations connected</span>
                </div>
              </div>
            </div>
          )}

          {currentStep === 9 && (
            <div className="space-y-4 text-center">
              <CheckCircle2 className="h-16 w-16 text-green-400 mx-auto" />
              <h3 className="text-2xl font-bold text-white/90">Congratulations!</h3>
              <p className="text-white/80">You've completed the onboarding wizard. You're ready to start using MedSpaBookings OS.</p>
              <Badge className="bg-green-500/20 text-green-300 border-green-500/30">Onboarding Complete</Badge>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between pt-6 border-t border-white/10">
            <Button
              onClick={handlePrevious}
              disabled={currentStep === 1}
              className="ghost-btn"
            >
              <ChevronLeft size={16} />
              Previous
            </Button>

            <div className="flex items-center gap-2">
              {currentStep < STEPS.length && currentStep !== 1 && (
                <Button onClick={handleSkip} className="ghost-btn">
                  Skip
                </Button>
              )}
              <Button onClick={handleNext} className="primary-btn">
                {currentStep === STEPS.length ? 'Finish' : 'Next'}
                <ChevronRight size={16} />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default OnboardingWizard;
